import React, { useEffect, useState } from "react";
import Modal from "./Modal.tsx";
import StyledButton from "./Button.tsx";
import {
  ModalInput,
  ModalLabel,
  ModalButtonRow,
  CancelButton,
  SmallText,
  ErrorMessage,
} from "./ModalFormComponents.tsx";
import { updateCustomCategory } from "../services/categories.service.ts";
import { parseApiError } from "../utils/errorHandler.ts";

interface EditCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: number;
  initialName: string;
  initialDescription?: string;
  onSuccess: (updated: {
    id: number;
    name: string;
    description?: string;
    conversationCount: number;
    pdfDocumentCount: number;
  }) => void;
}

export default function EditCategoryModal({
  isOpen,
  onClose,
  categoryId,
  initialName,
  initialDescription = "",
  onSuccess,
}: EditCategoryModalProps) {
  const [categoryName, setCategoryName] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCategoryName(initialName);
      setCategoryDescription(initialDescription ?? "");
      setError(null);
    }
  }, [isOpen, initialName, initialDescription]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const validateForm = (): boolean => {
    setError(null);
    if (!categoryName.trim()) {
      setError("Kategori navn er påkrævet");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const updated = await updateCustomCategory(categoryId, {
        name: categoryName.trim(),
        description: categoryDescription.trim(),
      });
      onSuccess(updated);
      handleClose();
    } catch (err: unknown) {
      setError(parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Rediger kategori">
      <SmallText>Opdater navn og beskrivelse for kategorien.</SmallText>
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ModalLabel>Navn på kategori *</ModalLabel>
      <ModalInput
        placeholder="Navn på kategorien"
        value={categoryName}
        onChange={(e) => setCategoryName(e.target.value)}
      />

      <ModalLabel>Beskrivelse</ModalLabel>
      <ModalInput
        placeholder="Beskrivelse af kategorien (valgfrit)"
        value={categoryDescription}
        onChange={(e) => setCategoryDescription(e.target.value)}
      />

      <ModalButtonRow>
        <CancelButton onClick={handleClose} disabled={saving}>
          Annuller
        </CancelButton>
        <StyledButton onClick={handleSave} disabled={saving}>
          {saving ? "Gemmer..." : "Gem"}
        </StyledButton>
      </ModalButtonRow>
    </Modal>
  );
}
